import "@/lib/polyfills";
import "./index.css";

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { BrowserGuard } from "@/components/BrowserGuard";
import { GlobalConfetti } from "@/components/GlobalConfetti";
import { Toaster } from "@/components/ui/sonner";
import { initAnalytics, setupNativeEventBridge } from "@/lib/analytics";
import { queryClient } from "@/lib/queryClient";
import { useAuthStore } from "@/store/authStore";

import { AppRouter } from "./router";

if (!import.meta.env.DEV) {
  Sentry.init({
    dsn: "https://e45a779b439cffce4881c4d228ec0097@o492713.ingest.us.sentry.io/4511088447913984",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
    enableLogs: true,

    beforeSend(event, hint) {
      const error = hint.originalException;
      const message = error instanceof Error ? error.message : String(error ?? "");

      if (message.includes("navigator.locks")) {
        return null;
      }
      if (message.includes("Purchase was cancelled")) {
        return null;
      }
      if (message.includes("PurchaseBridge")) {
        return null;
      }
      // Vexo analytics.js 외부 스크립트의 네트워크 실패 (제어 불가)
      if (message.includes("Bad network call")) {
        return null;
      }

      return event;
    },
  });
}

useAuthStore.getState().initialize();
initAnalytics();
setupNativeEventBridge();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="flex h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-lg font-semibold">문제가 발생했습니다</p>
          <p className="text-sm text-muted-foreground">잠시 후 다시 시도해 주세요.</p>
          <button
            type="button"
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            onClick={() => {
              resetError();
              window.location.reload();
            }}
          >
            새로고침
          </button>
        </div>
      )}
    >
      <BrowserGuard>
        <QueryClientProvider client={queryClient}>
          <AppRouter />
          <Toaster />
          <GlobalConfetti />
          <Analytics />
          <SpeedInsights />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </BrowserGuard>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
